import { action, computed, observable, makeObservable } from 'mobx';
import { MultibetPriceResponseItem } from 'src/domains/sportsbook/betting/state/multibetState/multibetItemState/MultibetPriceResponseItem';
import {
    MultibetOfferItemType,
    MultibetPostBodyItemType,
    MultibetStatusType,
    BetslipCommandType,
    MultibetSelectionApiType,
} from 'src/domains/sportsbook/betting/state/multibetState/Types';
import * as uuid from 'uuid';
import {
    MultibetPriceErrorType,
    MultibetPriceSuccessType,
    MultibetPriceSuspendedType,
    MultibetPriceExpiredType,
    getMultibetErrorMessage,
} from 'src/api/config/eventsMultibet/multibetGetPrice';
import { Amount } from 'src_common/common/amount/Amount';
import { ChannelType } from 'src/domains/sportsbook/betting/betting/types';
import { MultibetBetType } from 'src/domains/sportsbook/betting/betting/postPlaceBet';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { BetslipMultibetId } from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { StakeInputState2 } from 'src/domains/sportsbook/betting/betSlipState/stakeInputState/StakeInputState2';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { Common } from 'src/domains/common/Common';
import { ChannelState } from 'src/domains/sportsbook/betting/state/channelState/ChannelState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export class MultibetItemState {
    @observable.ref private selections: Array<MultibetSelectionApiType> = [];
    @observable.ref private priceResponseItem: MultibetPriceResponseItem;
    @observable private inputFieldValue: Amount = new Amount('0');

    public readonly stakeInput: StakeInputState2;
    public readonly id: BetslipMultibetId;
    public readonly correlationId: string = uuid.v4();
    public readonly fixtureId: string;
    public readonly sportsbookFixtureId: string;
    public readonly marketId: string;
    public readonly internalEventId: number;
    public readonly eventName: string;
    public readonly sport: string;

    private readonly sdkCustomer: SdkCustomer;
    private readonly languagesState: LanguagesState;
    private readonly channelState: ChannelState;
    private readonly decimalLength: number;

    public constructor(common: Common, command: BetslipCommandType) {
        makeObservable(this);

        this.sdkCustomer = SdkCustomer.get(common);
        this.languagesState = LanguagesState.get(common);
        this.channelState = ChannelState.get(common);
        this.decimalLength = ConfigComponents.get(common).config.decimalLength;

        this.fixtureId = command.sportsbookFixtureId ?? '';
        this.sportsbookFixtureId = command.sportsbookFixtureId ?? '';
        this.marketId = String(command.marketId ?? '');
        this.internalEventId = command.internalEventId ?? 0;
        this.eventName = command.eventName ?? '';
        this.sport = command.sport ?? 'football';

        // Initialize selections from command
        if (command.selections !== undefined && command.selections.length > 0) {
            this.selections = command.selections;
        }

        this.id = new BetslipMultibetId(this.correlationId, () => this);

        this.priceResponseItem = new MultibetPriceResponseItem(
            common,
            this.marketId,
            () => this.postBody,
            () => parseFloat(this.inputFieldValue.value) || 0
        );
        this.stakeInput = new StakeInputState2(this.decimalLength, this.setInputField);
    }

    private getChannel(): ChannelType {
        return this.channelState.channel;
    }

    public getFreeBets(
        possibleMultibetBets: Array<{ correlationId: string; freeBets?: Array<string> | null }>
    ): Array<string> {
        const multibetBet = possibleMultibetBets.find((bet) => bet.correlationId === this.correlationId);

        if (multibetBet === undefined) {
            return [];
        }

        return multibetBet.freeBets ?? [];
    }

    public get currentPrice(): MultibetPriceResponseItem {
        return this.priceResponseItem;
    }

    public acceptChanges(): void {
        this.priceResponseItem.acceptChanges();
    }

    @computed public get selectionChanged(): boolean {
        return this.priceResponseItem.selectionChanged;
    }

    @computed public get selectionChangedDown(): boolean {
        return this.priceResponseItem.selectionChangedDown;
    }

    public get inputField(): Amount {
        return this.inputFieldValue;
    }

    @action public setInputField = (value: Amount): void => {
        this.inputFieldValue = value;
    };

    @computed public get multibetPrice(): Amount {
        return this.inputFieldValue;
    }

    @computed public get multibetOffer(): Array<MultibetOfferItemType> {
        const out: Array<MultibetOfferItemType> = [];
        for (const selection of this.selections) {
            out.push({
                sportsbookMarketId: selection.sportsbookMarketTypeId,
                selection: selection,
            });
        }
        return out;
    }

    @computed public get currentPriceResponse():
        | null
        | MultibetPriceErrorType
        | MultibetPriceSuccessType
        | MultibetPriceSuspendedType
        | MultibetPriceExpiredType {
        return this.currentPrice.price;
    }

    @computed public get multibetStatus(): MultibetStatusType {
        const currentPriceResponse = this.currentPrice.price;

        if (currentPriceResponse === null) {
            return {
                price: null,
                priceString: null,
                conflictingBets: false,
                suspendedBets: false,
                expiredBets: false,
            };
        } else if (currentPriceResponse.type === 'error') {
            return {
                price: null,
                priceString: null,
                conflictingBets: true,
                suspendedBets: false,
                expiredBets: false,
                conflictingBetsMessage: getMultibetErrorMessage(this.languagesState, currentPriceResponse.data),
            };
        } else if (currentPriceResponse.type === 'suspended') {
            return {
                price: null,
                priceString: null,
                conflictingBets: false,
                suspendedBets: true,
                expiredBets: false,
            };
        } else if (currentPriceResponse.type === 'expired') {
            return {
                price: null,
                priceString: null,
                conflictingBets: false,
                suspendedBets: false,
                expiredBets: true,
                expiredMessage: currentPriceResponse.data.message,
            };
        } else if (currentPriceResponse.data.price > 0) {
            return {
                price: currentPriceResponse.data.price,
                priceString: currentPriceResponse.data.price.toFixed(2),
                conflictingBets: false,
                suspendedBets: false,
                expiredBets: false,
            };
        }

        return {
            price: null,
            priceString: null,
            conflictingBets: false,
            suspendedBets: false,
            expiredBets: false,
        };
    }

    @computed public get postBody(): Array<MultibetPostBodyItemType> {
        const out: Array<MultibetPostBodyItemType> = [];

        for (const selection of this.selections) {
            out.push({
                selection: selection,
                market: {
                    sportsbookMarketId: selection.sportsbookMarketTypeId,
                },
            });
        }

        return out;
    }

    public getMultibetBet(): MultibetBetType | null {
        const multibetPrice = this.currentPriceResponse;

        if (
            multibetPrice === null ||
            multibetPrice.type === 'error' ||
            multibetPrice.type === 'suspended' ||
            multibetPrice.type === 'expired'
        ) {
            return null;
        }

        const fractional = multibetPrice.data.priceFractional;
        const userIp = this.sdkCustomer.session.ipUser;
        const country = this.sdkCustomer.basicData.valueReady?.country ?? 'GB';

        // Construct bet name from selections
        const betName = this.selections.map((sel) => sel.selectionName ?? sel.selectionId).join(', ');

        return {
            type: 'BB',
            stakePerLine: parseFloat(this.inputField.value) || 0,
            eachWay: false,
            channel: this.getChannel(),
            country: country,
            ip: userIp ?? '',
            correlationId: this.correlationId,
            sport: this.sport,
            legs: [
                {
                    type: 'betBuilder',
                    legReference: {
                        geniusMarketId: this.marketId,
                        geniusFixtureId: this.sportsbookFixtureId,
                    },
                    provider: 'Genius',
                    internalEventId: this.internalEventId,
                    price: {
                        d: multibetPrice.data.price,
                        f: fractional,
                    },
                    priceType: 'fp',
                    name: betName,
                },
            ],
        };
    }
}
